import clsx from 'clsx'
import Image from 'next/image'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import Button from '~/src/components/Button'
import ModalDialog from '~/src/components/ModalDialog'
import { useAppContext } from '~/src/context/AppProvider'
import useGetTempUserId from '~/src/hooks/useGetTempUserId'
import useSubmitNumber from '~/src/hooks/useSubmitNumber'

export type InputData = {
  number: string
}

interface Props {
  reason?: 'issue' | 'price'
  onSubmitCallback?: (data?: InputData) => Promise<void> | void
}

const PhoneCaptureForm: React.FC<Props> = ({
  onSubmitCallback,
  reason = 'price',
}) => {
  const { hash, phoneSubmitted } = useAppContext()
  const { tempUserId } = useGetTempUserId()
  const [showError, setShowError] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)

  const { submit, processing, submitted, error } = useSubmitNumber()

  const { register, handleSubmit, watch, formState } = useForm<InputData>({
    defaultValues: {
      number: phoneSubmitted,
    },
  })

  const phoneError = formState.errors.number
  const numberWatch = watch('number')

  const onSubmit = async (data: InputData) => {
    if (!data.number) return
    if (!hash) return
    if (!tempUserId) return
    if (processing || submitted) return

    setShowConfirm(false)
    setShowError(false)

    await submit({
      hash,
      tempUserId,
      userNumber: data.number,
      reason,
    })

    if (onSubmitCallback) {
      await onSubmitCallback(data)
    }

    setShowConfirm(true)
  }

  useEffect(() => {
    setShowError(!!error)
  }, [error, processing])

  return (
    <div className='min-h-[108px] w-full'>
      {submitted ? (
        <div className='space-y-2 py-4 text-center'>
          <div className='text-4xl'>👍</div>
          <div className='opacity-60'>
            Thank you. We will be in touch shortly.
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className='w-full'>
          <fieldset
            className='w-full space-y-2'
            disabled={submitted || processing}
          >
            <div
              className={clsx(
                'dark:bg-darkinput flex w-full items-center overflow-hidden rounded-md border-2 bg-white pr-2',
                {
                  'border-black dark:border-white/50': !phoneError,
                  'border-red-500': phoneError,
                }
              )}
            >
              <div className='flex items-center justify-center self-stretch bg-gray-200 px-2'>
                <Image
                  src='/assets/us-flag.png'
                  alt='US'
                  width={35}
                  height={35}
                />
              </div>
              <div className='px-2 text-xl'>+1</div>
              <input
                type='tel'
                required
                placeholder='000 0000'
                className='w-full rounded-md border-none px-0 py-2 text-xl'
                {...register('number', {
                  required: true,
                  pattern: /^\d{3}[\s-]?\d{3}[\s-]?\d{4}$/,
                })}
              />
            </div>
            <Button
              type='submit'
              size='lg'
              className={clsx('w-full transition-opacity', {
                'opacity-0': !numberWatch,
                'opacity-100': !!numberWatch,
              })}
            >
              Text me
            </Button>
          </fieldset>
        </form>
      )}

      {submitted && (
        <ModalDialog show={showConfirm} onClose={() => setShowConfirm(false)}>
          {() => (
            <div className='p-6'>
              <div className='text-center font-semibold'>
                Thank you! We will be in touch.
              </div>
            </div>
          )}
        </ModalDialog>
      )}

      {error && (
        <ModalDialog show={showError} onClose={() => setShowError(false)}>
          {() => (
            <div className='p-6'>
              <div className='text-center font-semibold text-red-600'>
                {error}
              </div>
            </div>
          )}
        </ModalDialog>
      )}
    </div>
  )
}

export default PhoneCaptureForm
